import React, { useEffect, useState } from "react";
import { deleteFile, HttpService } from "../../services/httpService";
import { ReferenceDetail } from "../../interfaces/referenceDetaill";
import { GetAllGeneralReferenceDocuments } from "../../services/referenceDetailService";
import ConfirmDelete from "../component-tools/ConfirmDelete";

export interface ReferenceResponseFile {
  name: string;
}

interface DownloadGeneralReferenceDocsProps {
  referenceDetail: ReferenceDetail;
  onClose: (message?: string) => void;
}

const DownloadGeneralReferenceDocs: React.FC<
  DownloadGeneralReferenceDocsProps
> = ({ referenceDetail, onClose }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [files, setFiles] = useState<ReferenceResponseFile[]>([]);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileToDelete, setFileToDelete] = useState<string | null>(null);

  useEffect(() => {
    const fetchFiles = async () => {
      try {
        setLoadingSpinner(true);
        const data: ReferenceResponseFile[] =
          await GetAllGeneralReferenceDocuments(
            referenceDetail.referenceDetailID,
          );
        if (!data) {
          throw new Error("No response from server");
        }
        if (data.length === 0) {
          setFiles([]);
          setSelectedFile(null);
          return;
        }
        setFiles(data);
        setSelectedFile(data[0].name);
      } catch (error) {
        console.error("Error fetching reference document files:", error);
        setErrorMessage("Unable to fetch available documents.");
      } finally {
        setLoadingSpinner(false);
      }
    };

    fetchFiles();
  }, [referenceDetail.referenceDetailID]);

  const handleDownload = async () => {
    if (!selectedFile) return;
    try {
      setLoadingSpinner(true);
      const blobUrl = await HttpService.getFileAdmin(
        "reference-general-docs",
        referenceDetail.referenceDetailID.toString(),
        selectedFile,
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = selectedFile;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      setErrorMessage("Failed to download file.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleDeleteFile = async (fileName: string) => {
    try {
      setLoadingSpinner(true);
      const success = await deleteFile(
        "reference-general-docs",
        referenceDetail.referenceDetailID,
        fileName,
      );

      if (success) {
        const updatedFiles = files.filter((f) => f.name !== fileName);
        setFiles(updatedFiles);
        setFileToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting form:", error);
      setErrorMessage("An error occurred while deleting the form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <>
      <div className="background-mask" onClick={() => onClose()}></div>
      <div className="filter-popup-large">
        <div className="close-button-container">
          <button className="close-button" onClick={() => onClose()}>
            <i className="bi bi-x-circle"></i>
          </button>
        </div>
        <div className="filter-header">
          <h2>Download Reference Document</h2>
          <div>Please select a document to download.</div>
        </div>
        <br></br>
        <div className="dialog-container">
          <div className="file-list-container">
            {files.length === 0 ? (
              <div className="no-files">No documents available.</div>
            ) : (
              files.map((file) => (
                <React.Fragment key={file.name}>
                  <div
                    key={file.name}
                    className={`file-row ${
                      file.name === selectedFile ? "selected" : ""
                    }`}
                    onClick={() => setSelectedFile(file.name)}
                  >
                    <span className="file-name">{file.name}</span>
                    <i
                      className="bi bi-x file-delete-icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFileToDelete(file.name);
                      }}
                    />
                  </div>
                </React.Fragment>
              ))
            )}
          </div>
          <div className="action-buttons">
            <button
              className="smbtn btn-grey"
              onClick={handleDownload}
              disabled={!selectedFile}
            >
              Download Selected Document
            </button>
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </div>
      </div>
      {fileToDelete && (
        <ConfirmDelete
          onConfirm={() => {
            handleDeleteFile(fileToDelete);
          }}
          onCancel={() => {
            setFileToDelete(null);
          }}
          message="Delete this Document?"
        />
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </>
  );
};

export default DownloadGeneralReferenceDocs;
